import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/design-tokens.css';
import '../../styles/owner-dashboard.css';
import '../../styles/owner-layout.css';
import '../../styles/owner-ui.css'; /* Global Owner UI - Visual Identity */
import '../../styles/owner-theme.css'; /* Premium Owner Theme */
import '../../styles/owner-mobile.css'; /* Global mobile-first stylesheet */
import '../../styles/ownerLinksReportsPageHover.css'; /* Social icon hover/focus in More menu */
import AccountMenu from './AccountMenu.jsx';
import NotificationsDrawer from './NotificationsDrawer.jsx';
import DropboxNotificationBell from './DropboxNotificationBell.jsx';
import BookingDetailModal from './BookingDetailModal.jsx';
import { normalizeBookingForModal } from '../../utils/bookingDetailModalUtils.js';
import { useAttentionItems } from '../../contexts/AttentionItemsContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { BUILD_SHA, BUILD_TIME } from '../../config/buildInfo.js';

export default function OwnerLayout({ owner, onLogout, children, propertyName }) {
  const { attentionItems } = useAttentionItems();
  const { theme } = useTheme();
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // Global reservation modal (so the notification bell can open the same folio
  // modal that Calendar/Reservations use).
  const [reservationBooking, setReservationBooking] = useState(null);
  const openReservationModal = (booking) => setReservationBooking(normalizeBookingForModal(booking, booking?.listing_id));

  // Set build info on body for easy inspection
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-build', `${BUILD_SHA}_${BUILD_TIME}`);
    }
  }, []);

  // Get page title from location (Activity hidden — /property-activity redirects to /calendar)
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/calendar') return 'Calendar';
    if (path === '/reservations') return 'Reservations';
    if (path === '/financials') return 'Financials';
    if (path === '/analytics' || path === '/dashboard') return 'Dashboard';
    return 'Owner Portal';
  };

  // Get property display name
  const isActive = (path) => location.pathname === path;

  return (
    <>
      <div className="owner-layout-shell">
        {/* PHASE 2: Mobile shell - sticky container with two rows */}
        <div className="owner-layout-shell-header" style={isMobile ? styles.mobileShellHeader : {}}>
          {/* Row 1: Header (Logo + Page Title + Property Chip + Account) */}
          <header className="owner-layout-header" data-test="owner-global-header" style={isMobile ? styles.mobileHeaderRow : {}}>
            {/* Left: Logo */}
            <div style={isMobile ? styles.mobileHeaderLeft : styles.headerLeft}>
              <div style={isMobile ? styles.mobileBrand : styles.brand}>
                <img 
                  src="/brand/clc/logos/clc-logo-blue-768x768-png-cea5c7b9.webp" 
                  alt="CLC Vacation Homes" 
                  style={isMobile ? styles.mobileBrandLogo : styles.brandLogo}
                />
                {!isMobile && (
                  <>
                    <span style={styles.brandName}>CLC</span>
                    <span style={styles.brandDivider}>/</span>
                    <span style={styles.brandContext}>Owner Portal</span>
                  </>
                )}
                {isMobile && (
                  <>
                    <span style={styles.mobileBrandName}>CLC</span>
                    <span style={styles.mobileBrandDivider}>·</span>
                    <span style={styles.mobilePageTitle}>{getPageTitle()}</span>
                  </>
                )}
              </div>
            </div>

            {/* Center: Empty on mobile (tabs in row 2) */}
            {!isMobile && (
              <nav style={styles.headerNav}>
                <a
                  href="/calendar"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/calendar');
                  }}
                  aria-current={isActive('/calendar') ? 'page' : undefined}
                  className={isActive('/calendar') ? 'active' : ''}
                >
                  Calendar
                </a>
                <a
                  href="/reservations"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/reservations');
                  }}
                  aria-current={isActive('/reservations') ? 'page' : undefined}
                  className={isActive('/reservations') ? 'active' : ''}
                >
                  Reservations
                </a>
                <a
                  href="/financials"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/financials');
                  }}
                  aria-current={isActive('/financials') ? 'page' : undefined}
                  className={isActive('/financials') ? 'active' : ''}
                >
                  Financials
                </a>
                <a
                  href="/analytics"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/analytics');
                  }}
                  aria-current={isActive('/analytics') ? 'page' : undefined}
                  className={isActive('/analytics') ? 'active' : ''}
                >
                  Analytics
                </a>
                <a
                  href="/links-reports"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/links-reports');
                  }}
                  aria-current={isActive('/links-reports') ? 'page' : undefined}
                  className={isActive('/links-reports') ? 'active' : ''}
                >
                  Reports / Help
                </a>
                <DropboxNotificationBell onOpenReservation={openReservationModal} />
              </nav>
            )}

            {/* Right: Account Menu */}
            <div style={isMobile ? styles.mobileHeaderRight : styles.headerRight}>
              {owner && (
                <AccountMenu
                  owner={owner}
                  onLogout={onLogout}
                />
              )}
            </div>
          </header>

          {/* Row 2: Tabs (mobile only) */}
          {isMobile && (
            <nav className="owner-layout-tabs-mobile" style={styles.mobileTabsRow}>
              <a
                href="/calendar"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/calendar');
                }}
                aria-current={isActive('/calendar') ? 'page' : undefined}
                className={isActive('/calendar') ? 'active' : ''}
              >
                Calendar
              </a>
              <a
                href="/reservations"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/reservations');
                }}
                aria-current={isActive('/reservations') ? 'page' : undefined}
                className={isActive('/reservations') ? 'active' : ''}
              >
                Reservations
              </a>
              <a
                href="/financials"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/financials');
                }}
                aria-current={isActive('/financials') ? 'page' : undefined}
                className={isActive('/financials') ? 'active' : ''}
              >
                Financials
              </a>
              <a
                href="/analytics"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/analytics');
                }}
                aria-current={isActive('/analytics') ? 'page' : undefined}
                className={isActive('/analytics') ? 'active' : ''}
              >
                Analytics
              </a>
              <a
                href="/links-reports"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/links-reports');
                }}
                aria-current={isActive('/links-reports') ? 'page' : undefined}
                className={isActive('/links-reports') ? 'active' : ''}
              >
                Reports / Help
              </a>
              <DropboxNotificationBell onOpenReservation={openReservationModal} />
            </nav>
          )}
        </div>

        <main className="owner-layout-main">
          {children}
        </main>
        <footer className="owner-layout-footer">
          <p style={styles.footerText}>
            © 2026 CLC Vacation Homes: Owner's Portal
            <span style={{ marginLeft: 'var(--spacing-2)', fontFamily: 'monospace', fontSize: '0.85em', opacity: 0.9 }}>
              Build: {BUILD_SHA} {BUILD_TIME}
            </span>
          </p>
        </footer>
      </div>

      {/* Notifications Drawer */}
      <NotificationsDrawer
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        attentionItems={attentionItems}
        onItemClick={(item) => {
          // Navigation handled by DashboardPage
          setNotificationsOpen(false);
        }}
      />

      {/* Global reservation folio modal — opened from the notification bell */}
      <BookingDetailModal
        isOpen={!!reservationBooking}
        booking={reservationBooking}
        onClose={() => setReservationBooking(null)}
      />

    </>
  );
}

const styles = {
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    flex: '0 0 auto',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)', /* Reduced gap: 24px (was 32px) */
    fontSize: 'var(--font-size-lg)', /* Reduced from xl to lg for compact header */
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-header-text-brand, #FFFFFF)', /* Phase 1 Brand Alignment: White text on dark header */
    letterSpacing: '-0.02em', /* Tighter for premium feel */
  },
  brandLogo: {
    height: '40px', /* Reduced from 64px to 40px for compact header */
    width: 'auto',
    objectFit: 'contain',
    /* Phase 1.5: White logo for dark header - filter converts blue logo to white */
    filter: 'brightness(0) invert(1)', /* Invert blue logo to white for dark header */
  },
  brandName: {
    fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--font-size-xl)', /* Reduced from 2xl to xl for compact header */
    letterSpacing: '-0.03em',
  },
  verificationMarker: {
    marginLeft: 'var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-brand-primary)',
    fontWeight: 'var(--font-weight-bold)',
  },
  brandDivider: {
    color: 'rgba(255, 255, 255, 0.6)', /* Phase 1 Brand Alignment: Lighter white for divider on dark header */
  },
  brandContext: {
    fontWeight: 'var(--font-weight-semibold)', /* Increased from medium */
    color: 'rgba(255, 255, 255, 0.9)', /* Phase 1 Brand Alignment: White text on dark header */
    fontSize: 'var(--font-size-lg)', /* Slightly larger for better hierarchy */
    letterSpacing: '-0.01em',
  },
  propertyName: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-normal)', /* Phase 1.5: Typography alignment - corrected to CLC standard token */
    color: 'var(--color-text-secondary)',
  },
  headerNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    minWidth: 0,
    flexWrap: 'nowrap',
    padding: '0 var(--spacing-2)',
    /* No overflow - fixed 4 tabs + More menu */
  },
  /* navLink and navLinkActive styles removed - now handled by owner-layout.css */
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    flex: '0 0 auto',
    flexWrap: 'nowrap', /* Prevent wrapping */
    minWidth: 0,
    justifyContent: 'flex-end',
  },
  helpButton: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border-light)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
    flexShrink: 0,
    opacity: 0.75, /* De-emphasized for nav dominance */
  },
  // PHASE 2: Mobile shell - sticky container
  // Phase 1 Brand Alignment: Match desktop header with CLC Navy background
  mobileShellHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 200,
    backgroundColor: 'var(--color-header-bg-brand, #254860)',
    boxShadow: '0 2px 12px rgba(37, 72, 96, 0.15), 0 1px 3px rgba(0, 0, 0, 0.06)',
  },
  // PHASE 2: Mobile header row (row 1) - Compact for 48-56px target
  // Phase 1 Brand Alignment: Match desktop header background
  mobileHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px', // Reduced vertical padding: 6px top/bottom
    minHeight: '48px',
    maxHeight: '56px', // Enforce max height
    height: 'auto',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-header-bg-brand, #254860)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)', // Light border on dark background
    gap: '8px', // Minimal gap between elements
  },
  mobileHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
  },
  mobileBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  mobileBrandLogo: {
    height: '24px',
    width: 'auto',
    objectFit: 'contain',
    flexShrink: 0,
    /* Phase 1.5: White logo for dark header - filter converts blue logo to white */
    filter: 'brightness(0) invert(1)', /* Invert blue logo to white for dark header */
  },
  mobileBrandName: {
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-header-text-brand, #FFFFFF)',
    letterSpacing: '-0.02em',
    flexShrink: 0,
  },
  mobileBrandDivider: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 'var(--font-size-sm)',
    flexShrink: 0,
  },
  mobilePageTitle: {
    fontSize: 'var(--font-size-base, 16px)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-header-text-brand, #FFFFFF)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.2',
    maxHeight: '20px',
    minWidth: 0,
  },
  mobileHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '0 0 auto',
    flexShrink: 0,
  },
  // PHASE 2: Mobile tabs row (row 2)
  mobileTabsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    minHeight: '44px',
    height: '44px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
};

